import { TipsItemDto, TagDto } from './hittaTipsSidaService';
export type { TipsItemDto, TagDto };

export interface FilterChoiceDto {
  Name: string;
  Text: string;
  Value: string;
  Children: FilterChoiceDto[];
}

export interface FilterElementDto {
  Type: 'category' | 'list';
  FilterHeading: string;
  FilterTabName: string;
  QueryStringParameter: string;
  ShowModal: boolean;
  ModalHeading: string;
  ModalText: string;
  Choices: FilterChoiceDto[];
}

export interface FilterConfigDto {
  EnableFreeTextSearch: boolean;
  FreeTextHeading: string;
  Heading: string;
  ShowAllButton: boolean;
  FilterElements: FilterElementDto[];
}

export interface HanteraTipsItemDto extends TipsItemDto {
  FormSubmissonId: string;
  InskickadAvNamn: string;
  InskickadAvEpost: string;
  PubliceradDatumTid: string | null;
  UtgårDatum: string | null;
  Status: string;
}

export interface HanteraTipsPreviewDto {
  Id: number;
  FormSubmissonId: string;
  Rubrik: string;
  Ingress: string;
  BeskrivningHtml: string;
  Framtagare: string;
  LänkMaterial: string;
  InskickadAvNamn: string;
  InskickadAvEpost: string;
  InskickadDatumTid: string;
  PubliceradDatumTid: string | null;
  UtgårDatum: string | null;
  Status: string;
  SenastÄndradAv: string;
  EpiserverEditUrl: string | null;
  Nyckelord: TagDto[];
  Typer: TagDto[];
}

export interface CheckboxOptionDto {
  Id: number;
  Text: string;
  Selected: boolean;
}

export interface StatusOptionDto {
  Value: string;
  Text: string;
}

export interface HanteraTipsEditDto {
  TipsContentId: number;
  Rubrik: string;
  LänkMaterial: string;
  Ingress: string;
  BeskrivningHtml: string;
  Framtagare: string;
  Del1Granskad: boolean;
  Del2Granskad: boolean;
  Status: string;
  InskickadAvNamn: string;
  InskickadAvEpost: string;
  PubliceradDatumTid: string | null;
  UtgårDatum: string | null;
  SenastÄndradAv: string;
  IsPublished: boolean;
  NyckelordOptions: CheckboxOptionDto[];
  TypOptions: CheckboxOptionDto[];
  StatusOptions: StatusOptionDto[];
}

export interface HanteraTipsSaveRequest {
  ContentId: number;
  TipsContentId: number;
  Rubrik: string;
  LänkMaterial: string;
  Ingress: string;
  BeskrivningHtml: string;
  Framtagare: string;
  Del1Granskad: boolean;
  Del2Granskad: boolean;
  Status: string;
  InskickadAvNamn: string;
  InskickadAvEpost: string;
  Nyckelord: number[];
  Typ: number[];
  Publish: boolean;
}

export interface HanteraTipsResultsDto {
  TotalCount: number;
  CurrentPage: number;
  PageCount: number;
  PageSize: number;
  Items: HanteraTipsItemDto[];
}

export async function fetchFilterConfig(contentId: number): Promise<FilterConfigDto> {
  const res = await fetch(`/api/hanteratipssida/filter?contentId=${contentId}`);
  if (!res.ok) throw new Error(`Filter config fetch failed: ${res.status}`);
  return res.json();
}

export async function fetchResults(
  contentId: number,
  params: URLSearchParams,
  signal: AbortSignal
): Promise<HanteraTipsResultsDto> {
  const res = await fetch(
    `/api/hanteratipssida/results?contentId=${contentId}&${params.toString()}`,
    { signal }
  );
  if (!res.ok) throw new Error(`Results fetch failed: ${res.status}`);
  return res.json();
}

export async function fetchPreview(
  contentId: number,
  formId: string | null,
  tipsContentId: number
): Promise<HanteraTipsPreviewDto> {
  const params = new URLSearchParams();
  params.set('contentId', String(contentId));
  if (formId) params.set('formId', formId);
  if (tipsContentId > 0) params.set('tipsContentId', String(tipsContentId));
  const res = await fetch(`/api/hanteratipssida/preview?${params.toString()}`);
  if (!res.ok) throw new Error(`Preview fetch failed: ${res.status}`);
  return res.json();
}

export async function deleteTips(
  contentId: number,
  formId: string | null,
  tipsContentId: number
): Promise<void> {
  const params = new URLSearchParams();
  params.set('contentId', String(contentId));
  if (formId) params.set('formId', formId);
  if (tipsContentId > 0) params.set('tipsContentId', String(tipsContentId));
  const res = await fetch(`/api/hanteratipssida/delete?${params.toString()}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
}

export async function publishTips(
  contentId: number,
  formId: string | null,
  tipsContentId: number
): Promise<void> {
  const params = new URLSearchParams();
  params.set('contentId', String(contentId));
  if (formId) params.set('formId', formId);
  if (tipsContentId > 0) params.set('tipsContentId', String(tipsContentId));
  const res = await fetch(`/api/hanteratipssida/publish?${params.toString()}`, { method: 'POST' });
  if (!res.ok) throw new Error(`Publish failed: ${res.status}`);
}

export async function unpublishTips(contentId: number, tipsContentId: number): Promise<void> {
  const params = new URLSearchParams();
  params.set('contentId', String(contentId));
  params.set('tipsContentId', String(tipsContentId));
  const res = await fetch(`/api/hanteratipssida/unpublish?${params.toString()}`, { method: 'POST' });
  if (!res.ok) throw new Error(`Unpublish failed: ${res.status}`);
}

export async function updateTipsDate(contentId: number, tipsContentId: number): Promise<void> {
  const params = new URLSearchParams();
  params.set('contentId', String(contentId));
  params.set('tipsContentId', String(tipsContentId));
  const res = await fetch(`/api/hanteratipssida/updatedate?${params.toString()}`, { method: 'POST' });
  if (!res.ok) throw new Error(`Update date failed: ${res.status}`);
}

export async function fetchEditData(
  contentId: number,
  formId: string | null,
  tipsContentId: number
): Promise<HanteraTipsEditDto> {
  const params = new URLSearchParams();
  params.set('contentId', String(contentId));
  if (formId) params.set('formId', formId);
  if (tipsContentId > 0) params.set('tipsContentId', String(tipsContentId));
  const res = await fetch(`/api/hanteratipssida/edit?${params.toString()}`, { method: 'POST' });
  if (!res.ok) throw new Error(`Edit data fetch failed: ${res.status}`);
  return res.json();
}

export async function saveTips(request: HanteraTipsSaveRequest): Promise<void> {
  const res = await fetch('/api/hanteratipssida/save', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  if (!res.ok) throw new Error(`Save failed: ${res.status}`);
}
